import { useEffect } from 'react'

/**
 * Header options for ProductDetailScreen.
 *
 * ATL-963 — QARASAY-KASSA-26K, "item is null" in getFirestoreRef, reached from
 * `handleDelete (DeleteConfirmationModal) → handleDelete (ProductDetailScreen) → deleteProduct`.
 *
 * `makeACopy()` sets `productItem` to null (the copy is unsaved, so there is no product to
 * act on yet), but the `navigation.setOptions` that installs the header trash icon lived
 * INSIDE `if (productItem) { ... }`. When productItem went null the effect re-ran, the `if`
 * was false, setOptions was never called again — and the previously installed `headerRight`
 * closure, which had captured the old NON-null productItem, stayed mounted and visible. Its
 * own `productItem != null &&` guard was therefore always true: it tested the captured value,
 * not the current one. Tapping it called deleteProduct(db, null, null, uid) and threw.
 *
 * The fix is that the options are now recomputed and re-applied on EVERY relevant change,
 * which is only possible if the computation is unconditional. Keeping it in a separate,
 * pure function is what lets that be asserted in a test.
 *
 * @param {Object|null} productItem       the product being viewed, or null for an unsaved new/copy
 * @param {boolean} canDelete             the viewer holds the deleteProduct permission
 * @param {string} newProductTitle        title to show when there is no saved product
 * @param {Function} renderDeleteButton   () => JSX for the trash affordance
 * @returns {{headerRight: Function, title: string}}
 */
export const productDetailHeaderOptions = ({
  productItem,
  canDelete,
  newProductTitle,
  renderDeleteButton,
}) => ({
  // Always a function, never `undefined`: setOptions merges, so handing back undefined would
  // leave whatever was installed previously in place — the exact failure being fixed.
  headerRight: productItem && canDelete ? renderDeleteButton : () => null,
  title: productItem ? productItem.name : newProductTitle,
})

/**
 * Applies the options above to the navigator whenever any input changes.
 *
 * Deliberately a hook and deliberately unconditional: the 26K defect was not a wrong VALUE, it
 * was setOptions not being CALLED at all on the transition to a null productItem, which left a
 * stale headerRight closure — holding the previous, non-null product — mounted and tappable.
 * Never wrap the effect body (or this call) in an `if (productItem)`.
 */
export const useProductDetailHeader = (navigation, {
  productItem,
  canDelete,
  newProductTitle,
  renderDeleteButton,
}) => {
  useEffect(() => {
    navigation.setOptions(productDetailHeaderOptions({
      productItem,
      canDelete,
      newProductTitle,
      renderDeleteButton,
    }))
  }, [navigation, productItem, canDelete, newProductTitle, renderDeleteButton])
}
